import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiService } from '../../../core/services';
import { ApiError } from '../../../core/types/api';
import { handleMaintenance } from '../utils';
import { ACCOUNT_QUERY_KEYS, useGetProfile } from './queries';

interface AuthTokens {
  refreshToken: string;
  accessToken: string;
}

interface LoginRequest {
  username: string;
  password: string;
}

type UseLoginProps = {
  onSuccess?: (data: AuthTokens) => void;
  onError?: (error: ApiError) => void;
};

type UseLoginMutationProps = {
  username: string;
  password: string;
};

export const useLogin = ({ onSuccess, onError }: UseLoginProps = {}) => {
  return useMutation({
    mutationFn: ({ username, password }: UseLoginMutationProps) =>
      ApiService.post<AuthTokens, LoginRequest>('login', {
        username,
        password,
      }).then((response) => response.data),
    onSuccess: (data) => {
      if (onSuccess) {
        onSuccess(data);
      }
    },
    onError: (error) => {
      if (onError) {
        onError(error as ApiError);
      }
    },
  });
};

interface AccountInfoRetrivalRequest {
  email: string;
}

export const useRequestNewPassword = () => {
  return useMutation({
    mutationFn: (email: string) =>
      ApiService.post<void, AccountInfoRetrivalRequest>('forgot-password', {
        email,
      }),
  });
};

export const useRequestUsername = () => {
  return useMutation({
    mutationFn: (email: string) =>
      ApiService.post<void, AccountInfoRetrivalRequest>('forgot-username', {
        email,
      }),
  });
};

interface ResetPasswordRetrievalRequest {
  token: string;
  password: string;
}

interface ResetPasswordProps {
  token: string;
  password: string;
}

export const useResetPassword = () => {
  return useMutation({
    mutationFn: ({ token, password }: ResetPasswordProps) =>
      ApiService.post<void, ResetPasswordRetrievalRequest>('reset-password', {
        token,
        password,
      }),
  });
};

interface UseChangePasswordProps {
  onSuccess?: VoidFunction;
}

interface ChangePasswordProps {
  oldPassword: string;
  newPassword: string;
}

export const useChangePassword = ({ onSuccess }: UseChangePasswordProps = {}) => {
  const queryClient = useQueryClient();
  const { refetch: fetchProfile } = useGetProfile({ enabled: false });

  return useMutation({
    mutationFn: ({ oldPassword, newPassword }: ChangePasswordProps) =>
      ApiService.post<void, ChangePasswordProps>('me/change-password', {
        oldPassword,
        newPassword,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ACCOUNT_QUERY_KEYS.ME] });
      fetchProfile();

      if (onSuccess) {
        onSuccess();
      }
    },
  });
};

interface UseAcceptTOSProps {
  onSuccess?: VoidFunction;
}

export const useAcceptTOS = ({ onSuccess }: UseAcceptTOSProps = {}) => {
  const queryClient = useQueryClient();
  const { refetch: fetchProfile } = useGetProfile({ enabled: false });

  return useMutation({
    mutationFn: () => ApiService.put<void, { accept: boolean }>('me/terms', { accept: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ACCOUNT_QUERY_KEYS.ME] });
      fetchProfile();

      if (onSuccess) {
        onSuccess();
      }
    },
  });
};

interface UseCheckMaintenanceProps {
  onSuccess?: (data: string | boolean) => void;
}

export interface MaintenanceProps {
  next_maintenance_at: string;
}

export const useCheckMaintenance = ({ onSuccess }: UseCheckMaintenanceProps = {}) => {
  return useMutation({
    mutationFn: () =>
      ApiService.post<MaintenanceProps, void>('maintenance').then((response) => handleMaintenance(response.data)),
    onSuccess: (data) => {
      if (onSuccess) {
        onSuccess(data);
      }
    },
  });
};

export const useSetSignedImageCookie = () => {
  return useMutation({
    mutationFn: () =>
      ApiService.get<null>('v2/assets/signed-cookie', {
        withCredentials: true,
      }),
  });
};
